import type { IncidentDomainResponse } from '@prerender/diagnostics-api';
import { useId, useState } from 'react';
import { Form } from 'react-bootstrap';

import CopyableQuery from './CopyableQuery';

type AffectedUrlsProps = {
  domains: readonly IncidentDomainResponse[];
};

type AffectedUrlQuery = {
  readonly domain: string;
  readonly query: string;
};

const TITLE = 'Affected URLs';
const RUN_IN_CONSOLE_HINT =
  "Run this in the ClickHouse Cloud console to list the picked domain's affected URLs. Append FORMAT CSV to get a file you can send the customer.";
const COPY_LABEL = 'Copy the affected-URL query';
const DOMAIN_PICKER_LABEL = 'Domain';

function affectedUrlQueriesOf(domains: readonly IncidentDomainResponse[]): AffectedUrlQuery[] {
  return domains.flatMap(({ domain, affectedUrlQuery }) =>
    typeof affectedUrlQuery === 'string' ? [{ domain, query: affectedUrlQuery }] : []
  );
}

function pickedQuery(queries: readonly AffectedUrlQuery[], pickedDomain: string | null): AffectedUrlQuery | undefined {
  return queries.find(({ domain }) => domain === pickedDomain);
}

function AffectedUrls({ domains }: AffectedUrlsProps) {
  const titleId = useId();
  const [pickedDomain, setPickedDomain] = useState<string | null>(null);
  const queries = affectedUrlQueriesOf(domains);
  const picked = pickedQuery(queries, pickedDomain);
  if (pickedDomain !== null && picked === undefined) setPickedDomain(null);

  const shown = picked ?? queries[0];
  if (shown === undefined) return null;

  return (
    <section className="diagnostics-detail-affected-urls" aria-labelledby={titleId}>
      <h2 id={titleId} className="diagnostics-detail-section-title">
        {TITLE}
      </h2>
      <p className="diagnostics-affected-url-hint">{RUN_IN_CONSOLE_HINT}</p>
      <Form.Select
        className="diagnostics-affected-url-domain"
        aria-label={DOMAIN_PICKER_LABEL}
        value={shown.domain}
        onChange={(event) => setPickedDomain(event.target.value)}
      >
        {queries.map(({ domain }) => (
          <option key={domain} value={domain}>
            {domain}
          </option>
        ))}
      </Form.Select>
      <CopyableQuery sql={shown.query} copyLabel={COPY_LABEL} />
    </section>
  );
}

export default AffectedUrls;
